// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — Export Utilities (Excel & PDF Reports)
// ─────────────────────────────────────────────────────────────────────────────
// Production-ready data export engine.
// Excel: column-config driven, auto-width, ₹ currency format, date DD/MM/YYYY.
// PDF:   teal brand header, autotable, summary totals, page numbering.
//
// Known limitation: xlsx OSS (v0.18.5) does not support cell background styling.
// Header row will be bold text only — no background color in Excel.
// ─────────────────────────────────────────────────────────────────────────────

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brandConfig, getBrandLogoCanvasDataUrl } from '@/config/brandConfig';
import { COLOR, LOGO_BASE64 } from './pdfConstants';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ExportColumnFormat = 'text' | 'currency' | 'number' | 'date' | 'integer';

export interface ExportColumn {
  /** Column header label */
  header: string;
  /** Key to access value from data record */
  key: string;
  /** Optional width in characters (Excel) or mm (PDF) */
  width?: number;
  /** Format to apply when rendering the cell value */
  format?: ExportColumnFormat;
}

export interface ExcelSummaryMetric {
  label: string;
  value: string | number;
  format?: ExportColumnFormat;
}

export interface ExcelExportOptions {
  fileName: string;
  sheetName?: string;
  /** Organisation name shown in the top heading row */
  orgName?: string;
  columns?: ExportColumn[];
  headers?: string[];
  /** All records (not paginated) — object array or 2D array */
  data: Record<string, unknown>[] | (string | number)[][];
  filtersSummary?: string;
  exportDate?: string;
  includeTotalsRow?: boolean;
  summaryMetrics?: ExcelSummaryMetric[];
}

export interface PdfExportOptions {
  fileName: string;
  title: string;
  subtitle?: string;
  filtersSummary?: string;
  columns?: ExportColumn[];
  headers?: string[];
  rows: Record<string, unknown>[] | (string | number)[][];
  summaryLines?: { label: string; value: string }[];
  orientation?: 'portrait' | 'landscape';
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export function formatCurrency(amount: number | string | null | undefined): string {
  const num =
    typeof amount === 'number'
      ? amount
      : parseFloat((amount as string | null | undefined)?.toString() ?? '0') || 0;
  const isNegative = num < 0;
  const absVal = Math.abs(num);
  const [int = '0', dec = '00'] = absVal.toFixed(2).split('.');
  const lastThree = int.slice(-3);
  const rest = int.slice(0, -3);
  const formatted = rest
    ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
    : lastThree;
  return `${isNegative ? '-' : ''}Rs. ${formatted}.${dec}`;
}

function formatCellValue(
  value: unknown,
  format: ExportColumnFormat = 'text'
): string | number {
  if (value === null || value === undefined || value === '') return '—';

  switch (format) {
    case 'currency': {
      const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      return formatCurrency(num);
    }
    case 'number': {
      const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(num);
    }
    case 'integer': {
      const num = typeof value === 'number' ? value : parseInt(String(value)) || 0;
      return num;
    }
    case 'date': {
      // Accept ISO (YYYY-MM-DD) or date string → output DD/MM/YYYY
      const s = String(value);
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y, m, d] = s.split('T')[0]!.split('-');
        return `${d}/${m}/${y}`;
      }
      return s;
    }
    default:
      return String(value);
  }
}

/**
 * Like formatCellValue, but returns a typed cell (real number/Date + an Excel
 * number format) instead of a pre-formatted string. Writing real numeric/date
 * types — rather than text like "Rs. 1,500.00" — lets Excel apply its default
 * alignment (numbers/dates right, text left) instead of everything left-aligned,
 * and keeps the values sortable/calculable.
 */
function formatCellForSheet(
  value: unknown,
  format: ExportColumnFormat = 'text'
): { v: string | number | Date; z?: string } {
  if (value === null || value === undefined || value === '') return { v: '—' };

  switch (format) {
    case 'currency': {
      const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      return { v: num, z: '"₹"#,##0.00;[RED]-"₹"#,##0.00' };
    }
    case 'number': {
      const num = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
      return { v: num, z: '#,##0.00' };
    }
    case 'integer': {
      const num = typeof value === 'number' ? value : parseInt(String(value)) || 0;
      return { v: num, z: '#,##0' };
    }
    case 'date': {
      const s = String(value);
      const d = new Date(s);
      if (!isNaN(d.getTime())) return { v: d, z: 'dd/mm/yyyy' };
      return { v: s };
    }
    default: {
      const s = String(value);
      const lower = s.toLowerCase();
      if (lower.includes('unpaid')) {
        return { v: s, z: '[Red]@' };
      }
      if (lower.includes('return')) {
        return { v: s, z: '[Magenta]@' };
      }
      return { v: s };
    }
  }
}

/**
 * Sanitizes a string for use as an Excel worksheet name.
 * Excel prohibits: \ / ? * : [ ]
 * Length cannot exceed 31 characters, cannot be empty, and cannot start/end with single quote.
 */
export function sanitizeExcelSheetName(name?: string, fallback = 'Report'): string {
  if (!name || typeof name !== 'string') return fallback;
  // Replace prohibited characters: \ / ? * : [ ]
  let clean = name.replace(/[\\/?*:[\]]/g, ' ').replace(/\s+/g, ' ').trim();
  // Strip leading/trailing single quotes
  clean = clean.replace(/^'+|'+$/g, '').trim();
  // Truncate to 31 chars
  clean = clean.slice(0, 31).trim();
  return clean || fallback;
}

/**
 * Sanitizes a string for use as a file name across Windows/macOS/Linux.
 * Prohibits filesystem characters: < > : " / \ | ? * and ASCII control characters (0-31).
 */
export function sanitizeFileName(name?: string, fallback = 'export'): string {
  if (!name || typeof name !== 'string') return fallback;
  let clean = name.replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').replace(/_+/g, '_').trim();
  return clean || fallback;
}

// ─── Excel Export ─────────────────────────────────────────────────────────────

/**
 * Exports records to a branded .xlsx workbook.
 *
 * Row layout:
 *   Row 1 — Org name + report title
 *   Row 2 — Export date
 *   Row 3 — (empty separator)
 *   Row 4 — Column headers (ALL CAPS)
 *   Row 5+ — Data rows
 *   (if filtersSummary): appended after headers as a context row
 */
export function exportToExcel({
  fileName,
  sheetName = 'Report',
  orgName,
  columns,
  headers,
  data,
  filtersSummary,
  exportDate,
  includeTotalsRow,
  summaryMetrics,
}: ExcelExportOptions): void {
  if (!data || data.length === 0) {
    throw new Error('No records are available to export for the selected filters.');
  }

  const dateStr =
    exportDate ??
    new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const aoa: (string | number | Date)[][] = [];
  const pendingFormats: { r: number; c: number; z: string }[] = [];

  // Row 1: Org + brand heading
  const headingText = orgName
    ? `${orgName} — ${brandConfig.name} Export`
    : `${brandConfig.name} — ${sheetName}`;
  aoa.push([headingText]);

  // Row 2: Export date
  aoa.push([`Exported: ${dateStr}`]);

  // Row 3: Filter context (if any)
  if (filtersSummary) {
    aoa.push([`Filters: ${filtersSummary}`]);
  } else {
    aoa.push([]); // empty separator
  }

  // Row 4: Headers (ALL CAPS)
  const headerLabels: string[] = columns
    ? columns.map((c) => c.header.toUpperCase())
    : (headers ?? []).map((h: string) => h.toUpperCase());
  aoa.push(headerLabels);

  // Data rows
  data.forEach((record) => {
    if (Array.isArray(record)) {
      aoa.push(record as (string | number)[]);
    } else if (columns) {
      const rowIndex = aoa.length;
      const row = columns.map((col, colIdx) => {
        const raw = record[col.key];
        const { v, z } = formatCellForSheet(raw, col.format);
        if (z) pendingFormats.push({ r: rowIndex, c: colIdx, z });
        return v;
      });
      aoa.push(row);
    } else {
      aoa.push(Object.values(record) as (string | number)[]);
    }
  });

  // ── Append Totals row if enabled or if numeric columns are present ──
  const shouldAddTotals = includeTotalsRow ?? (columns ? columns.some((c) => c.format === 'currency' || c.format === 'number') : false);
  if (shouldAddTotals && columns && columns.length > 0) {
    const totalsRowIndex = aoa.length;
    const totalsRow = columns.map((col, colIdx) => {
      if (colIdx === 0) return `TOTAL (${data.length} records)`;
      if (col.format === 'currency' || col.format === 'number') {
        const sum = data.reduce((acc, row) => {
          const rawVal = Array.isArray(row) ? row[colIdx] : (row as Record<string, unknown>)[col.key];
          const num = typeof rawVal === 'number' ? rawVal : parseFloat(String(rawVal ?? '0')) || 0;
          return acc + num;
        }, 0);
        const { v, z } = formatCellForSheet(sum, col.format);
        if (z) pendingFormats.push({ r: totalsRowIndex, c: colIdx, z });
        return v;
      }
      if (col.format === 'integer') {
        const sum = data.reduce((acc, row) => {
          const rawVal = Array.isArray(row) ? row[colIdx] : (row as Record<string, unknown>)[col.key];
          const num = typeof rawVal === 'number' ? rawVal : parseInt(String(rawVal ?? '0')) || 0;
          return acc + num;
        }, 0);
        const { v, z } = formatCellForSheet(sum, col.format);
        if (z) pendingFormats.push({ r: totalsRowIndex, c: colIdx, z });
        return v;
      }
      return '';
    });
    aoa.push(totalsRow);
  }

  // ── Append Dedicated Summary Metrics Section ───────────────────────
  if (summaryMetrics && summaryMetrics.length > 0) {
    aoa.push([]); // empty spacer row
    aoa.push(['SUMMARY DETAILS', 'VALUE']);
    summaryMetrics.forEach((metric) => {
      const metricRowIdx = aoa.length;
      const { v, z } = formatCellForSheet(metric.value, metric.format ?? (typeof metric.value === 'number' ? 'number' : 'text'));
      if (z) pendingFormats.push({ r: metricRowIdx, c: 1, z });
      aoa.push([metric.label, v]);
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa, { cellDates: true });

  // Apply Excel number formats so currency/number/date columns render with
  // proper formatting and right-align natively instead of as plain text.
  pendingFormats.forEach(({ r, c, z }) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (ws[addr]) ws[addr].z = z;
  });

  // Auto-fit column widths from content (including totals)
  const colsToMeasure = columns || headerLabels.map((h: string) => ({ header: h, key: h }));
  const colWidths = colsToMeasure.map((col: ExportColumn | { header: string; key: string }, idx: number) => {
    let maxLen = col.header.length;
    aoa.forEach((rowArr) => {
      const cellVal = rowArr[idx];
      if (cellVal !== undefined && cellVal !== null) {
        maxLen = Math.max(maxLen, String(cellVal).length);
      }
    });
    const configWidth = ('width' in col ? (col as ExportColumn).width : 0) ?? 0;
    return { wch: Math.min(Math.max(maxLen + 4, configWidth || 12), 55) };
  });
  ws['!cols'] = colWidths;

  // Freeze top 4 rows (heading + date + separator + header row)
  ws['!freeze'] = { xSplit: 0, ySplit: filtersSummary ? 4 : 4 };

  const wb = XLSX.utils.book_new();
  const safeSheet = sanitizeExcelSheetName(sheetName);
  XLSX.utils.book_append_sheet(wb, ws, safeSheet);

  const baseFileName = fileName.replace(/\.xlsx$/i, '');
  const safeFileName = sanitizeFileName(baseFileName, 'report');
  const cleanName = `${safeFileName}.xlsx`;
  XLSX.writeFile(wb, cleanName);
}

// ─── PDF Export ───────────────────────────────────────────────────────────────

/**
 * Exports records to a branded, multi-page PDF report.
 * Header: teal brand band (RGB 6,182,212).
 * Table: autoTable striped with teal header row.
 * Footer: page number on every page.
 */
export function exportToPdf({
  fileName,
  title,
  subtitle,
  filtersSummary,
  columns,
  headers,
  rows,
  summaryLines,
  orientation = 'portrait',
}: PdfExportOptions): void {
  if (!rows || rows.length === 0) {
    throw new Error('No records are available to export for the selected filters.');
  }

  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageWidth = orientation === 'portrait' ? 210 : 297;
  const pageHeight = orientation === 'portrait' ? 297 : 210;

  // ── 1. Premium Ink Header Banner with gold accent ──────────────
  doc.setFillColor(...COLOR.ink);
  doc.rect(0, 0, pageWidth, 22, 'F');

  // Gold accent beside the embedded brand mark.
  doc.setFillColor(...COLOR.gold);
  doc.rect(12, 5, 1.4, 12, 'F');

  try {
    const logoUrl = LOGO_BASE64 || getBrandLogoCanvasDataUrl();
    if (logoUrl) {
      doc.addImage(logoUrl, 'PNG', 16, 3, 16, 16);
    }
  } catch (e) {
    console.warn('Could not add logo to PDF report:', e);
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(brandConfig.name, pageWidth - 14, 8, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR.goldLight);
  doc.text(brandConfig.tagline, pageWidth - 14, 13, { align: 'right' });
  doc.text(
    `Generated: ${new Date().toLocaleDateString('en-IN')}  ·  ${brandConfig.address}, ${brandConfig.cityStateZip}`,
    pageWidth - 14,
    18,
    { align: 'right' }
  );

  // Gold rule beneath the banner
  doc.setDrawColor(...COLOR.gold);
  doc.setLineWidth(0.6);
  doc.line(0, 22, pageWidth, 22);

  // ── 2. Report Title ───────────────────────────────────────────
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  let currentY = 30;
  doc.text(title, 14, currentY);

  if (subtitle) {
    currentY += 5;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(subtitle, 14, currentY);
  }

  if (filtersSummary) {
    currentY += 5;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text(`Filters: ${filtersSummary}`, 14, currentY);
    currentY += 5;
  }

  currentY += 2;

  // ── 3. AutoTable ──────────────────────────────────────────────
  const rawHeaders: string[] = columns ? columns.map((c) => c.header) : (headers ?? []);
  const tableHeaders = rawHeaders.map((c: string) => c.replace(/₹/g, 'Rs.'));
  const tableRows = rows.map((record) => {
    if (Array.isArray(record)) {
      return record.map((cell) => (typeof cell === 'string' ? cell.replace(/₹/g, 'Rs.') : cell));
    }
    if (columns) {
      return columns.map((col) => {
        const raw = record[col.key];
        return formatCellValue(raw, col.format) as string | number;
      });
    }
    return Object.values(record) as (string | number)[];
  });

  const columnStyles = columns
    ? columns.reduce(
        (acc: Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number }>, col, i) => {
          const colStyle: { halign?: 'left' | 'right' | 'center'; cellWidth?: number } = {};
          if (col.format === 'currency' || col.format === 'number') {
            colStyle.halign = 'right';
          } else if (col.format === 'integer') {
            colStyle.halign = 'center';
          }
          if (col.width) {
            colStyle.cellWidth = col.width;
          }
          acc[i] = colStyle;
          return acc;
        },
        {} as Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number }>
      )
    : tableHeaders.reduce(
        (acc: Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number }>, h: string, i: number) => {
          const lower = h.toLowerCase();
          if (
            lower.includes('price') ||
            lower.includes('amount') ||
            lower.includes('total') ||
            lower.includes('rs.')
          ) {
            acc[i] = { halign: 'right' };
          } else if (
            lower.includes('qty') ||
            lower.includes('quantity') ||
            lower.includes('count')
          ) {
            acc[i] = { halign: 'center' };
          }
          return acc;
        },
        {} as Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number }>
      );

  autoTable(doc, {
    startY: currentY,
    margin: { left: 14, right: 14 },
    tableWidth: pageWidth - 28,
    head: [tableHeaders],
    body: tableRows,
    theme: 'striped',
    headStyles: {
      fillColor: COLOR.ink,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      cellPadding: { top: 3.5, right: 3, bottom: 3.5, left: 3 },
    },
    bodyStyles: {
      textColor: [30, 41, 59],
      fontSize: 8,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
    },
    alternateRowStyles: {
      fillColor: COLOR.rowAlt,
    },
    columnStyles,
    styles: { overflow: 'linebreak' },
    didDrawPage: (data) => {
      const totalPages = doc.getNumberOfPages();
      doc.setDrawColor(...COLOR.gold);
      doc.setLineWidth(0.3);
      doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${data.pageNumber} of ${totalPages}  ·  ${brandConfig.reportFooterMessage}`,
        pageWidth / 2,
        pageHeight - 7,
        { align: 'center' }
      );
    },
  });

  // ── 4. Summary Totals ─────────────────────────────────────────
  if (summaryLines && summaryLines.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const finalY = (doc as any).lastAutoTable?.finalY ?? currentY + 30;
    let lineY = finalY + 8;

    const cardWidth = 76;
    const cardX = pageWidth - 14 - cardWidth;
    const cardHeight = summaryLines.length * 6 + 4;

    if (lineY + cardHeight > pageHeight - 15) {
      doc.addPage();
      lineY = 20;
    }

    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.roundedRect(cardX, lineY - 2, cardWidth, cardHeight, 1.5, 1.5, 'FD');
    doc.setFillColor(...COLOR.gold);
    doc.rect(cardX, lineY - 2, 1.4, cardHeight, 'F');

    let currentLineY = lineY + 3.5;
    summaryLines.forEach((line) => {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`${line.label}:`, cardX + 4, currentLineY);

      const cleanVal = line.value.replace(/₹/g, 'Rs. ');
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text(cleanVal, cardX + cardWidth - 4, currentLineY, { align: 'right' });
      currentLineY += 6;
    });
  }

  const baseFileName = fileName.replace(/\.pdf$/i, '');
  const safeFileName = sanitizeFileName(baseFileName, 'report');
  const cleanName = `${safeFileName}.pdf`;
  doc.save(cleanName);
}
